import { makeAutoObservable, runInAction } from "mobx";

import { createInvite, teamInvites } from "@/api";
import { InviteModel } from "@/models/InviteModel";
import { TeamModel } from "@/models/TeamModel";

import type { CreateInviteBodyKind } from "@/api";
import type { RootStore } from "@/store/RootStore";

type InvitesStatus = "loading" | "loaded";

export class InvitesUX {
  private rootStore: RootStore;

  invitesStatus: InvitesStatus = "loaded";

  team: TeamModel;

  invites: InviteModel[] = [];

  constructor(rootStore: RootStore, team?: TeamModel) {
    this.rootStore = rootStore;
    if (!team) {
      // eslint-disable-next-line no-param-reassign
      team = new TeamModel(this.rootStore, {
        typename: "TeamShort",
        uid: "",
        title: "",
        bio: null,
        permalink: "",
        personal: false,
        kind: "regular",
        avatarUrls: null,
        followersCount: 0,
        unreadMessagesCount: 0,
        membersCount: 0,
        userPermissions: { read: true, write: true, invite: true, billing: true },
      });
    }
    this.team = team;
    makeAutoObservable(this);
  }

  setInvitesStatus(status: InvitesStatus) {
    this.invitesStatus = status;
  }

  get linkInvites(): InviteModel[] {
    return this.invites.filter((invite) => Boolean(invite.link));
  }

  // people invites only; link invites render in their own "Invite links" section
  get visibleInvites(): InviteModel[] {
    const memberUsernames = new Set(
      this.team.membersUX.members.map((member) => member.user.username),
    );
    return this.invites.filter(
      (invite) =>
        (invite.user || invite.email) &&
        (!invite.user || !memberUsernames.has(invite.user.username)),
    );
  }

  async loadInvites(signal: AbortSignal) {
    this.setInvitesStatus("loading");
    const { invites } = await teamInvites(this.team.permalink, { signal });
    if (invites) {
      runInAction(() => {
        this.invites = invites.map((i) => new InviteModel(this.rootStore, i, this.team));
      });
    }
    this.setInvitesStatus("loaded");
  }

  async createInvite(
    query: { username: string } | { email: string } | { kind: CreateInviteBodyKind },
  ) {
    const res = await createInvite(this.team.permalink, query);
    if (res.invite && !this.invites.some((i) => i.token === res.invite!.token)) {
      runInAction(() => {
        this.invites.push(new InviteModel(this.rootStore, res.invite!, this.team));
      });
    }
    return res;
  }
}
